/**
 * The status of a campaign request as a badge, in words a brand understands. Once a deposit has
 * funded the request, the badge follows its campaign as the escrow reports it: funded, then
 * settled, refunded or cancelled.
 */

import { Badge } from "../components/ui.js";
import type { Campaign, CampaignRequest, RequestStatus } from "./requests.js";

type Tone = "green" | "amber" | "red" | "grey" | "blue";

const STATUS: Record<RequestStatus, { label: string; tone: Tone }> = {
  submitted: { label: "Waiting for review", tone: "blue" },
  accepted: { label: "Awaiting deposit", tone: "green" },
  rejected: { label: "Rejected", tone: "red" },
  withdrawn: { label: "Withdrawn", tone: "grey" },
  lapsed: { label: "Lapsed", tone: "amber" },
  funded: { label: "Funded", tone: "green" },
};

/** A funded request's campaign, by the escrow's status. The same words as the admin pages. */
const CAMPAIGN_STATUS: Record<Campaign["status"], { label: string; tone: Tone }> = {
  funded: { label: "Funded", tone: "green" },
  settled: { label: "Settled", tone: "blue" },
  refunded: { label: "Refunded", tone: "amber" },
  cancelled: { label: "Cancelled", tone: "grey" },
};

export function StatusBadge({
  request,
}: {
  request: Pick<CampaignRequest, "status" | "campaign">;
}) {
  const { label, tone } =
    request.status === "funded" && request.campaign
      ? CAMPAIGN_STATUS[request.campaign.status]
      : STATUS[request.status];
  return <Badge tone={tone}>{label}</Badge>;
}
